import { useMemo } from "react";
import { getLayout } from "@/hardware/keyboard-layouts/index";
import { builtinEntries, builtins } from "@/hardware/keyboards/builtin";
import { type CatalogEntry, catalogEntries } from "@/platform/catalog";
import { buildShareUrl, type LensView } from "@/platform/share/link";

/**
 * The share link for the current selection: the active Preset's canonical path
 * (downloadEntry, present only for a catalog Preset) plus the lens as view-params. In
 * compare it requires BOTH sides be Presets - a B-side upload is not URL-addressable.
 * A session-imported board is omitted from the lens (only built-ins are durable ids), so the link
 * still shares the Preset, just on the viewer's own board. Null when nothing shareable is selected.
 */
export function useShareLink({
  downloadEntry,
  comparing,
  sc2SourceBId,
  effBoardId,
  keyboardLayoutId,
  boardBId,
  layoutBId,
}: {
  downloadEntry: CatalogEntry | null;
  comparing: boolean;
  sc2SourceBId: string;
  effBoardId: string;
  keyboardLayoutId: string;
  boardBId: string | null;
  layoutBId: string | null;
}): string | null {
  return useMemo(() => {
    if (!downloadEntry) return null;
    if (comparing && !catalogEntries.some((e) => e.id === sc2SourceBId)) return null;
    const lens: LensView = {};
    if (builtins[effBoardId]) {
      lens.kbd = effBoardId;
      lens.layout = keyboardLayoutId;
    }
    if (comparing) {
      lens.cmp = true;
      lens.pb = sc2SourceBId;
      const bBuiltin = boardBId && builtins[boardBId] ? boardBId : null;
      if (bBuiltin) lens.kbdB = bBuiltin;
      // layoutB must be resolvable by a recipient: a universal layout id always is, and a board
      // layer id is when kbdB names the built-in that carries it. A session import's layer id
      // would land as a dead param (picker fallback to qwerty, a blank-looking select), so it is
      // omitted the same way kbdB omits the imported board itself.
      if (
        layoutBId &&
        (getLayout(layoutBId) ||
          (bBuiltin && builtinEntries[bBuiltin]?.boardLayers.some((l) => l.id === layoutBId)))
      ) {
        lens.layoutB = layoutBId;
      }
    }
    return buildShareUrl(downloadEntry.routePath, lens);
  }, [downloadEntry, comparing, sc2SourceBId, effBoardId, keyboardLayoutId, boardBId, layoutBId]);
}
